import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { 
  CreditCard, Smartphone, ArrowLeft, Crown, Star, Gem, User,
  Check, Loader2, AlertCircle, Phone
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CheckoutPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const preselectedPlan = searchParams.get('plan');
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);

  // Format duration consistently with /tarifs
  const formatDuration = (days) => {
    if (!days || days === 0) return 'Illimité';
    if (days >= 90) return `${Math.floor(days / 30)} mois`;
    if (days >= 30) return '30 jours';
    return `${days} jours`;
  };
  const [loading, setLoading] = useState(true);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [phone, setPhone] = useState('');
  const [currentUser, setCurrentUser] = useState(null);
  
  // Payment dialog state
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [paymentResult, setPaymentResult] = useState(null);
  const [confirmingPayment, setConfirmingPayment] = useState(false);

  useEffect(() => {
    fetchCategories();
    loadUserData();
  }, []);

  const loadUserData = () => {
    try {
      const userData = localStorage.getItem('user');
      if (userData) {
        setCurrentUser(JSON.parse(userData));
      }
    } catch (e) {
      console.error('Error loading user data:', e);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/categories`);
      // Filter out free category for checkout
      const paidCategories = (response.data.categories || []).filter(c => c.cost > 0);
      setCategories(paidCategories);
      // Auto-select plan from query string if provided
      if (preselectedPlan) {
        const match = paidCategories.find(c => c.id === preselectedPlan);
        if (match) setSelectedCategory(match);
      }
    } catch (error) {
      toast.error('Erreur lors du chargement des catégories');
    } finally {
      setLoading(false);
    }
  };

  const getCategoryIcon = (categoryId) => {
    switch (categoryId) {
      case 'diamond': return <Gem className="h-6 w-6" />;
      case 'gold': return <Star className="h-6 w-6" />;
      case 'premium': return <Crown className="h-6 w-6" />;
      default: return <User className="h-6 w-6" />;
    }
  };

  const getCategoryGradient = (categoryId) => {
    switch (categoryId) {
      case 'diamond': return 'from-cyan-400 to-cyan-600';
      case 'gold': return 'from-yellow-400 to-yellow-600';
      case 'premium': return 'from-amber-400 to-amber-600';
      default: return 'from-slate-400 to-slate-600';
    }
  };

  const handleSelectCategory = (category) => {
    setSelectedCategory(category);
  };

  const initiatePayment = async () => {
    if (!selectedCategory) {
      toast.error('Veuillez sélectionner une catégorie');
      return;
    }
    
    if (!phone || phone.length < 10) {
      toast.error('Veuillez entrer un numéro de téléphone valide');
      return;
    }

    const token = localStorage.getItem('token');
    if (!token) {
      toast.error('Veuillez vous connecter');
      navigate('/auth');
      return;
    }

    setPaymentLoading(true);
    try {
      const response = await axios.post(
        `${API}/payment/category/initiate`,
        null,
        {
          params: {
            category_id: selectedCategory.id,
            phone: phone
          },
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (response.data.success) {
        setPaymentResult(response.data);
        setPaymentDialogOpen(true);
      } else {
        toast.error(response.data.error || 'Erreur lors de l\'initialisation du paiement');
      }
    } catch (error) {
      console.error('Payment error:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors du paiement');
    } finally {
      setPaymentLoading(false);
    }
  };

  const confirmPayment = async () => {
    if (!paymentResult?.transaction_id) return;

    setConfirmingPayment(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/payment/category/confirm/${paymentResult.transaction_id}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        toast.success(response.data.message);
        
        // Update local user data
        const userData = localStorage.getItem('user');
        if (userData) {
          const user = JSON.parse(userData);
          user.category_id = selectedCategory.id;
          user.category_expires = response.data.expires;
          user.is_premium = true;
          localStorage.setItem('user', JSON.stringify(user));
        }
        
        setPaymentDialogOpen(false);
        navigate('/dashboard');
      } else {
        toast.error(response.data.message || 'Erreur lors de la confirmation');
      }
    } catch (error) {
      toast.error('Erreur lors de la confirmation du paiement');
    } finally {
      setConfirmingPayment(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-white flex items-center justify-center">
        <Loader2 className="h-8 w-8 text-sky-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-white py-12 px-4">
      <div className="max-w-6xl mx-auto">
        <Button
          variant="ghost"
          onClick={() => navigate(-1)}
          className="mb-6"
          data-testid="back-button"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Retour
        </Button>

        <div className="text-center mb-10">
          <h1 className="text-3xl md:text-4xl font-bold text-slate-900 mb-3">
            Choisissez votre formule
          </h1>
          <p className="text-slate-600 max-w-2xl mx-auto">
            Débloquez des fonctionnalités exclusives pour booster votre recherche d'emploi en RDC
          </p>
        </div>

        {/* Categories Grid */}
        <div className="grid md:grid-cols-3 gap-6 mb-10">
          {categories.map((category) => {
            const isSelected = selectedCategory?.id === category.id;
            const isCurrentPlan = currentUser?.category_id === category.id;
            
            return (
              <Card 
                key={category.id}
                className={`relative overflow-hidden cursor-pointer transition-all duration-300 ${
                  isSelected 
                    ? 'ring-2 ring-sky-500 shadow-xl scale-[1.02]' 
                    : 'hover:shadow-lg hover:scale-[1.01]'
                } ${isCurrentPlan ? 'opacity-60' : ''}`}
                onClick={() => !isCurrentPlan && handleSelectCategory(category)}
                data-testid={`category-card-${category.id}`}
              >
                {/* Header with gradient */}
                <div className={`bg-gradient-to-r ${getCategoryGradient(category.id)} p-6 text-white`}>
                  <div className="flex items-center justify-between mb-2">
                    {getCategoryIcon(category.id)}
                    {category.id === 'gold' && (
                      <Badge className="bg-white/20 text-white border-0">Populaire</Badge>
                    )}
                    {isCurrentPlan && (
                      <Badge className="bg-white/20 text-white border-0">Actuel</Badge>
                    )}
                  </div>
                  <h3 className="text-2xl font-bold">{category.name}</h3>
                  <p className="text-white/80 text-sm">{category.name_fr}</p>
                </div>

                <CardContent className="p-6">
                  {/* Price */}
                  <div className="mb-6">
                    <span className="text-4xl font-bold text-slate-900">${category.cost}</span>
                    <span className="text-slate-500 ml-2">
                      / {formatDuration(category.validity_days)}
                    </span>
                  </div>

                  {/* Features */}
                  <ul className="space-y-3 mb-6">
                    <li className="flex items-start gap-2">
                      <Check className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                      <span className="text-sm text-slate-600">
                        {category.services?.max_notifications_monthly === -1 
                          ? 'Notifications illimitées' 
                          : `${category.services?.max_notifications_monthly} notifications/mois`}
                      </span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                      <span className="text-sm text-slate-600">
                        Canaux: {category.services?.notification_channels?.join(', ')}
                      </span>
                    </li>
                    {category.services?.cv_upload && (
                      <li className="flex items-start gap-2">
                        <Check className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                        <span className="text-sm text-slate-600">Upload de CV</span>
                      </li>
                    )}
                    {category.services?.featured_badge && (
                      <li className="flex items-start gap-2">
                        <Check className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                        <span className="text-sm text-slate-600">Badge {category.name}</span>
                      </li>
                    )}
                    {category.services?.priority_support && (
                      <li className="flex items-start gap-2">
                        <Check className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                        <span className="text-sm text-slate-600">Support prioritaire</span>
                      </li>
                    )}
                    <li className="flex items-start gap-2">
                      <Check className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                      <span className="text-sm text-slate-600">
                        Visibilité: {category.services?.profile_visibility}
                      </span>
                    </li>
                  </ul>

                  {isSelected && (
                    <div className="absolute top-3 right-3">
                      <div className="w-6 h-6 bg-sky-500 rounded-full flex items-center justify-center">
                        <Check className="h-4 w-4 text-white" />
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Payment Section */}
        {selectedCategory && (
          <Card className="max-w-xl mx-auto" data-testid="payment-section">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Smartphone className="h-5 w-5 text-green-600" />
                Paiement Mobile Money
              </CardTitle>
              <CardDescription>
                Payez avec Vodacom M-Pesa, Airtel Money ou Orange Money
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Order Summary */}
              <div className="p-4 bg-slate-50 rounded-lg">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-slate-600">Catégorie sélectionnée:</span>
                  <span className="font-semibold">{selectedCategory.name}</span>
                </div>
                <div className="flex justify-between items-center mb-2">
                  <span className="text-slate-600">Validité:</span>
                  <span className="font-semibold">{formatDuration(selectedCategory.validity_days)}</span>
                </div>
                <div className="border-t pt-2 mt-2 flex justify-between items-center">
                  <span className="font-semibold text-lg">Total à payer:</span>
                  <span className="font-bold text-2xl text-sky-600">
                    ${selectedCategory.cost} USD
                  </span>
                </div>
              </div>

              {/* Phone Input */}
              <div>
                <Label htmlFor="phone">Numéro Mobile Money *</Label>
                <div className="flex gap-2 mt-1">
                  <div className="flex items-center px-3 bg-slate-100 rounded-l-md border border-r-0">
                    <Phone className="h-4 w-4 text-slate-500 mr-1" />
                    <span className="text-sm text-slate-600">+243</span>
                  </div>
                  <Input
                    id="phone"
                    type="tel"
                    placeholder="XXX XXX XXX"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    className="rounded-l-none"
                    data-testid="phone-input"
                  />
                </div>
                <p className="text-xs text-slate-500 mt-1">
                  Vodacom, Airtel, Orange, Africell
                </p>
              </div>

              {/* Simulation Notice */}
              <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-3">
                <AlertCircle className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-amber-800">Mode Simulation</p>
                  <p className="text-xs text-amber-700 mt-1">
                    Les paiements sont actuellement simulés. En production, vous recevrez 
                    une demande de paiement sur votre téléphone via MobiPay.cd
                  </p>
                </div>
              </div>

              {/* Pay Button */}
              <Button
                onClick={initiatePayment}
                disabled={paymentLoading || !phone}
                className="w-full bg-green-600 hover:bg-green-700 text-white py-6 text-lg font-semibold"
                data-testid="pay-button"
              >
                {paymentLoading ? (
                  <>
                    <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                    Initialisation...
                  </>
                ) : (
                  <>
                    <Smartphone className="h-5 w-5 mr-2" />
                    Payer ${selectedCategory.cost} via Mobile Money
                  </>
                )}
              </Button>
            </CardContent>
          </Card>
        )}

        {/* No Selection Prompt */}
        {!selectedCategory && (
          <div className="text-center text-slate-500 py-8">
            <CreditCard className="h-12 w-12 mx-auto mb-3 text-slate-300" />
            <p>Sélectionnez une catégorie pour continuer</p>
          </div>
        )}
      </div>

      {/* Payment Confirmation Dialog */}
      <Dialog open={paymentDialogOpen} onOpenChange={setPaymentDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Smartphone className="h-5 w-5 text-green-600" />
              Confirmer le paiement
            </DialogTitle>
            <DialogDescription>
              Transaction: {paymentResult?.transaction_id}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* Payment Instructions */}
            <div className="p-4 bg-green-50 rounded-lg border border-green-200">
              <h4 className="font-semibold text-green-900 mb-2">Instructions de paiement</h4>
              <p className="text-sm text-green-800 whitespace-pre-line">
                {paymentResult?.instructions || 
                  `En mode simulation, cliquez sur "Confirmer" pour activer votre abonnement.`}
              </p>
            </div>

            {/* Payment Details */}
            <div className="p-4 bg-slate-50 rounded-lg space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-slate-600">Montant:</span>
                <span className="font-semibold">
                  ${paymentResult?.amount} {paymentResult?.currency}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-600">Téléphone:</span>
                <span className="font-semibold">{paymentResult?.phone_number}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-600">Statut:</span>
                <Badge className="bg-yellow-100 text-yellow-800">
                  {paymentResult?.status === 'pending' ? 'En attente' : paymentResult?.status}
                </Badge>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => setPaymentDialogOpen(false)}
                className="flex-1"
              >
                Annuler
              </Button>
              <Button
                onClick={confirmPayment}
                disabled={confirmingPayment}
                className="flex-1 bg-green-600 hover:bg-green-700"
                data-testid="confirm-payment-button"
              >
                {confirmingPayment ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Confirmation...
                  </>
                ) : (
                  <>
                    <Check className="h-4 w-4 mr-2" />
                    Confirmer le paiement
                  </>
                )}
              </Button>
            </div>

            <p className="text-xs text-center text-slate-500">
              En cliquant sur Confirmer, vous acceptez les conditions d'utilisation de Mosala.cd
            </p>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CheckoutPage;
